import { ThemeProvider as NextThemeProvider } from 'next-themes';
import { memo, type ReactNode } from 'react';

import { useShareLinkForcedThemeStore } from '~/components/v2/store/features/share-link-theme/useShareLinkForcedThemeStore';
import { THEME } from '~/constants/localStorageKeys';

export type AppThemeProviderProps = {
  children: ReactNode;
  /**
   * The theme the active page forces, as derived by its own static `getForcedTheme(pageProps)`
   * (see `NextPageWithLayout` in `_app`). `undefined` = the visitor's own preference stays in charge.
   */
  forcedPageTheme?: 'light' | 'dark';
};

/*
 * The app's single next-themes provider (extracted from `_app` so the forcing decision is a
 * mountable, testable unit). It owns per-share-link theme forcing via next-themes' `forcedTheme`,
 * which next-themes applies to `document.documentElement` — so `dark:` variants, the semantic
 * color CSS vars, the `<body>` background painted by `useBodyBackground`, `style.colorScheme`
 * (native scrollbars/inputs), and every body-portalled surface (modals, menus, toasts) all follow.
 * next-themes also guarantees the restore: when `forcedTheme` goes back to `undefined` its apply
 * effect re-stamps the visitor's own stored theme, and while forced, its storage/media listeners
 * can't clobber the page.
 *
 * Two feeds, first one wins:
 * 1. `useShareLinkForcedThemeStore` — written while a share-link surface that knows its `colorMode`
 *    is mounted. Covers client-only data: passcode-verified links and the editor preview's live
 *    draft (the preview is its own document, so its own AppThemeProvider serves it).
 * 2. `forcedPageTheme` — the active page's own `getForcedTheme` derivation, evaluated by `_app` in
 *    the same SSR render (so next-themes' inline script still stamps the forced theme BEFORE first
 *    paint — no visitor-theme flash on dark links). Redundant with the store after hydration.
 */
export const AppThemeProvider = memo(({ children, forcedPageTheme }: AppThemeProviderProps) => {
  const shareLinkForcedTheme = useShareLinkForcedThemeStore((state) => state.forcedTheme);
  const forcedTheme = shareLinkForcedTheme ?? forcedPageTheme;

  return (
    <NextThemeProvider
      defaultTheme="light"
      disableTransitionOnChange
      forcedTheme={forcedTheme}
      storageKey={THEME}
      themes={['dark', 'light']}
    >
      {children}
    </NextThemeProvider>
  );
});

AppThemeProvider.displayName = 'AppThemeProvider';
